import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV } from "@/data/content";
import { useLenis, scrollToId } from "@/lib/lenis";
import { EASE } from "./Reveal";

export const Nav = () => {
  const lenis = useLenis();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(lenis, id);
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: EASE }}
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${scrolled ? "border-b border-white/10 bg-brand-navy/80 py-3 backdrop-blur-xl" : "py-6"}`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 lg:px-10">
        <button data-testid="nav-logo-malansh" onClick={() => go("top")} className="text-left" aria-label="Malansh home">
          <Logo dark testId="nav-logo-mark" />
        </button>

        <nav className="hidden items-center gap-1 rounded-full px-2 py-1.5 lg:flex">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-link-${n.id}`}
              onClick={() => go(n.id)}
              className="rounded-full px-4 py-2 font-body text-sm text-white/70 transition-colors duration-300 hover:bg-white/10 hover:text-white"
            >
              {n.label}
            </button>
          ))}
          <Link data-testid="nav-link-careers" to="/careers" className="rounded-full px-4 py-2 font-body text-sm text-white/70 transition-colors duration-300 hover:bg-white/10 hover:text-white">Careers</Link>
        </nav>

        <div className="flex items-center gap-3">
          <button data-testid="nav-cta-enquire" onClick={() => go("contact")} className="btn-primary hidden !py-2.5 !px-5 sm:inline-flex">
            <span>Enquire</span>
          </button>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen((o) => !o)}
            className="glass grid h-11 w-11 place-items-center rounded-full text-white lg:hidden"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="glass mx-6 mt-3 flex flex-col rounded-3xl p-3 lg:hidden"
          >
            {NAV.map((n) => (
              <button
                key={n.id}
                data-testid={`nav-mobile-link-${n.id}`}
                onClick={() => go(n.id)}
                className="rounded-2xl px-4 py-3 text-left font-display text-lg text-white/85 hover:bg-white/10"
              >
                {n.label}
              </button>
            ))}
            <Link data-testid="nav-mobile-link-careers" to="/careers" className="rounded-2xl px-4 py-3 text-left font-display text-lg text-white/85 hover:bg-white/10">Careers</Link>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
